/**
 * Formats an Event object into a descriptive error message.
 */
const formatEventError = (event: Event): string => {
  const eventType = event.type || 'unknown';
  const targetInfo = event.target
    ? `${event.target.constructor?.name || 'unknown'}`
    : 'unknown';
  return `Event error: type=${eventType}, target=${targetInfo}`;
};

/**
 * Extracts a meaningful error message from an object.
 */
const extractErrorMessageFromObject = (
  errorObj: Record<string, unknown>,
): string => {
  const messageFields = ['message', 'error', 'reason', 'name'] as const;
  const messageValue = messageFields
    .map((field) => errorObj[field])
    .find((value) => typeof value === 'string');

  if (typeof messageValue === 'string') {
    return messageValue;
  }

  try {
    const stringified = JSON.stringify(errorObj, null, 2);
    // Only use JSON if it's not just an empty object and not too long
    if (stringified !== '{}' && stringified.length < 1000) {
      return stringified;
    }
  } catch {
    // If JSON.stringify fails (circular reference, etc.), fall through
  }

  return `Error object: ${errorObj.constructor?.name || 'Object'}`;
};

/**
 * Converts an unknown error to a meaningful string message.
 * Handles Error instances, strings, plain objects, Events, and other types.
 */
const errorToString = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message || error.name || 'Error';
  }

  if (typeof error === 'string') {
    return error;
  }

  if (error == null) {
    return 'Unknown error (null or undefined)';
  }

  if (error instanceof Event) {
    return formatEventError(error);
  }

  if (typeof error === 'object') {
    return extractErrorMessageFromObject(error as Record<string, unknown>);
  }

  // Fallback for primitive types (numbers, booleans, symbols, bigint, etc.)
  const stringified = String(error);
  // Guard against edge cases where String() might produce '[object Object]'
  if (stringified === '[object Object]') {
    return 'Unknown error (object could not be stringified)';
  }
  return stringified;
};

/**
 * Normalizes an unknown error into a proper Error object with a meaningful message.
 */
export const coalesceError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }
  return new Error(errorToString(error));
};
